const SUBJECT_LABELS = {
  all: 'Все',
  role: 'Роли',
  responsible: 'Ответственный',
  route_approver: 'Согласующий по маршруту',
  uploader: 'Загрузивший файл',
}

export function normalizeSubjectForFlow(subject, cardFlow) {
  const value = subject || 'all'
  if (value === 'route_approver' && cardFlow !== 'approvals') {
    return 'all'
  }
  return value
}

export function normalizeChoiceItems(raw) {
  if (!raw) return []
  if (Array.isArray(raw)) {
    return raw.map((item) => {
      if (Array.isArray(item)) return item
      if (item && typeof item === 'object') {
        const value = item.value ?? item.value_id ?? item.id ?? item.key
        const label = item.label ?? item.title ?? item.name ?? item.text ?? String(value ?? '')
        return [value, label]
      }
      return [item, String(item)]
    })
  }
  if (Array.isArray(raw.items)) return raw.items
  return []
}

export function getChoiceItems(choices, choiceName) {
  if (!choices) return []
  if (Array.isArray(choices)) {
    const found = choices.find((x) => x && (x.name === choiceName || x.choice_name === choiceName))
    return normalizeChoiceItems(found && (found.items || found.values || found))
  }
  return normalizeChoiceItems(choices[choiceName])
}

export function choiceLabel(items, value) {
  if (value == null || value === '') return '—'
  const found = items.find((item) => {
    const v = Array.isArray(item) ? item[0] : item?.value
    return String(v) === String(value)
  })
  if (!found) return String(value)
  return Array.isArray(found) ? found[1] : (found.label ?? String(value))
}

export function subjectLabel(subject) {
  return SUBJECT_LABELS[subject] || subject || '—'
}

export function fileTypeLabels(fileTypes, fileTypeItems) {
  const types = Array.isArray(fileTypes) ? fileTypes.filter(Boolean) : []
  if (types.length === 0) return ['Все типы']
  return types.map((t) => choiceLabel(fileTypeItems, t))
}

export function groupRowsByStage(rows, cardFlow) {
  const groups = new Map()
  ;(rows || []).forEach((row, idx) => {
    const stage = (row || {}).stage
    if (!stage) return
    const key = String(stage)
    if (!groups.has(key)) {
      groups.set(key, { stage: key, rules: [] })
    }
    groups.get(key).rules.push({ row, idx })
  })
  const list = Array.from(groups.values())
  list.sort((a, b) => a.stage.localeCompare(b.stage, 'ru'))
  if (cardFlow) {
    list.sort((a, b) => {
      const pa = a.stage.startsWith(`${cardFlow}:`) ? 0 : 1
      const pb = b.stage.startsWith(`${cardFlow}:`) ? 0 : 1
      return pa - pb || a.stage.localeCompare(b.stage, 'ru')
    })
  }
  return list
}

/** OR-склейка правил (как на бэкенде, без привязки к конкретному файлу). */
export function mergeRuleAccess(rules) {
  let readAll = false
  let readOwn = false
  let upload = false
  let view = false
  let download = false
  let deletePerm = false

  ;(rules || []).forEach((row) => {
    const r = row.row || row
    if (r.can_read) {
      if (r.subject === 'uploader') readOwn = true
      else readAll = true
    }
    if (r.can_view) view = true
    if (r.can_download) download = true
    if (r.can_upload) upload = true
    if (r.can_delete) deletePerm = true
  })

  const read = readAll || readOwn
  let filesScope = 'none'
  let filesScopeLabel = '—'
  if (readAll) {
    filesScope = 'all'
    filesScopeLabel = 'все файлы этапа'
  } else if (readOwn) {
    filesScope = 'own'
    filesScopeLabel = 'только свои вложения'
  }

  return {
    read,
    upload: read && upload,
    view: read && view,
    download: read && download,
    delete: read && deletePerm,
    filesScope,
    filesScopeLabel,
  }
}

export function normalizeStageRuleEntries(stageRules) {
  return (stageRules || []).map((entry) => {
    if (entry && entry.row) {
      return { row: entry.row, idx: entry.idx }
    }
    return { row: entry, idx: undefined }
  })
}

/** Строки, адресованные только этой категории (как в карточках сверху). */
export function rulesOwnedByAudience(stageRules, audience) {
  return normalizeStageRuleEntries(stageRules).filter(({ row }) => {
    if (audience.type === 'all') return row.subject === 'all'
    if (audience.type === 'role') {
      return row.subject === 'role' && (row.roles_lnk || []).includes(audience.roleId)
    }
    if (audience.type === 'responsible') return row.subject === 'responsible'
    if (audience.type === 'uploader') return row.subject === 'uploader'
    if (audience.type === 'route_approver') return row.subject === 'route_approver'
    return false
  })
}

export function rulesMatchingAudience(stageRules, audience) {
  return normalizeStageRuleEntries(stageRules)
    .filter(({ row }) => {
      if (audience.type === 'all') return row.subject === 'all'
      if (audience.type === 'role') {
        return row.subject === 'all'
          || (row.subject === 'role' && (row.roles_lnk || []).includes(audience.roleId))
      }
      if (audience.type === 'responsible') {
        return row.subject === 'all' || row.subject === 'responsible'
      }
      if (audience.type === 'uploader') {
        return row.subject === 'all' || row.subject === 'uploader'
      }
      if (audience.type === 'route_approver') {
        return row.subject === 'all' || row.subject === 'route_approver'
      }
      return false
    })
    .map((x) => x.row)
}

export function ruleNumbers(entries) {
  return (entries || [])
    .map((entry) => {
      const idx = entry.idx
      return typeof idx === 'number' ? `#${idx + 1}` : null
    })
    .filter(Boolean)
}

export function accessSignature(access) {
  return [
    access.read ? 1 : 0,
    access.upload ? 1 : 0,
    access.view ? 1 : 0,
    access.download ? 1 : 0,
    access.delete ? 1 : 0,
    access.filesScopeLabel,
  ].join('|')
}

export function ruleTargetsAudience(row, audience) {
  if (audience.type === 'all') return row.subject === 'all'
  if (audience.type === 'role') {
    return row.subject === 'role' && (row.roles_lnk || []).includes(audience.roleId)
  }
  if (audience.type === 'responsible') return row.subject === 'responsible'
  if (audience.type === 'uploader') return row.subject === 'uploader'
  if (audience.type === 'route_approver') return row.subject === 'route_approver'
  return false
}

export function ruleAppliesToFileType(row, fileTypeKey) {
  const types = row.file_types_lnk || []
  if (!fileTypeKey) return true
  if (types.length === 0) return true
  return types.includes(fileTypeKey)
}

export function collectStageFileTypeSlices(stageRules, fileTypeItems) {
  const keys = new Set()
  let hasUniversal = false
  normalizeStageRuleEntries(stageRules).forEach(({ row }) => {
    const types = row.file_types_lnk || []
    if (types.length === 0) hasUniversal = true
    else types.forEach((t) => keys.add(t))
  })
  if (keys.size === 0) {
    return [{ key: null, label: 'Все типы файлов' }]
  }
  const slices = []
  if (hasUniversal) {
    slices.push({ key: null, label: 'Все типы (общие правила)' })
  }
  Array.from(keys).sort().forEach((key) => {
    slices.push({ key, label: choiceLabel(fileTypeItems, key) })
  })
  return slices
}

export function filterStageRulesByFileType(stageRules, fileTypeKey) {
  return normalizeStageRuleEntries(stageRules).filter(({ row }) => (
    ruleAppliesToFileType(row, fileTypeKey)
  ))
}

export function audienceTitle(audience, roleItems) {
  if (audience.type === 'role') {
    return `Роль: ${choiceLabel(roleItems, audience.roleId)}`
  }
  return audience.title
}

export function emptyProfile() {
  return { roleIds: [], responsible: false, uploader: false, routeApprover: false }
}

export function profileOptionsForStage(stageRules) {
  const roleIds = new Set()
  let hasResponsible = false
  let hasUploader = false
  let hasRouteApprover = false
  normalizeStageRuleEntries(stageRules).forEach(({ row }) => {
    if (row.subject === 'role') {
      ;(row.roles_lnk || []).forEach((id) => roleIds.add(id))
    }
    if (row.subject === 'responsible') hasResponsible = true
    if (row.subject === 'uploader') hasUploader = true
    if (row.subject === 'route_approver') hasRouteApprover = true
  })
  return {
    roleIds: Array.from(roleIds),
    hasResponsible,
    hasUploader,
    hasRouteApprover,
  }
}

export function ruleMatchesProfile(row, profile) {
  const subject = row.subject || 'all'
  if (subject === 'all') return true
  if (subject === 'role') {
    const ruleRoles = row.roles_lnk || []
    const selected = profile.roleIds || []
    return ruleRoles.some((id) => selected.includes(id))
  }
  if (subject === 'responsible') return !!profile.responsible
  if (subject === 'uploader') return !!profile.uploader
  if (subject === 'route_approver') return !!profile.routeApprover
  return false
}

export function rulesMatchingProfile(stageRules, profile) {
  return normalizeStageRuleEntries(stageRules)
    .filter(({ row }) => ruleMatchesProfile(row, profile))
    .map((x) => x.row)
}

export function profileTitle(profile, roleItems) {
  const parts = []
  ;(profile.roleIds || []).forEach((id) => {
    parts.push(choiceLabel(roleItems, id))
  })
  if (profile.responsible) parts.push('Ответственный в карточке')
  if (profile.uploader) parts.push('Автор вложения')
  if (profile.routeApprover) parts.push('Согласующий по маршруту')
  if (parts.length === 0) return 'Без особых признаков'
  return parts.join(' + ')
}

export function profileHasTraits(profile) {
  return (profile.roleIds || []).length > 0
    || profile.responsible
    || profile.uploader
    || profile.routeApprover
}

export function isRowProfilePickable(row) {
  const subject = row.subject || 'all'
  return subject !== 'all'
}

export function toggleRowInProfile(row, profile) {
  const subject = row.subject || 'all'
  if (subject === 'all') return profile
  if (subject === 'role') {
    const ruleRoles = row.roles_lnk || []
    const selected = new Set(profile.roleIds || [])
    const isActive = ruleRoles.some((id) => selected.has(id))
    if (isActive) {
      ruleRoles.forEach((id) => selected.delete(id))
    } else {
      ruleRoles.forEach((id) => selected.add(id))
    }
    return { ...profile, roleIds: Array.from(selected) }
  }
  if (subject === 'responsible') {
    return { ...profile, responsible: !profile.responsible }
  }
  if (subject === 'uploader') {
    return { ...profile, uploader: !profile.uploader }
  }
  if (subject === 'route_approver') {
    return { ...profile, routeApprover: !profile.routeApprover }
  }
  return profile
}

export function audiencesForStage(stageRules) {
  const rows = (stageRules || []).map((x) => x.row || x)
  const audiences = [{ type: 'all', title: 'Любой пользователь' }]
  const roleIds = new Set()
  rows.forEach((row) => {
    if (row.subject === 'role') {
      ;(row.roles_lnk || []).forEach((id) => roleIds.add(id))
    }
  })
  roleIds.forEach((roleId) => {
    audiences.push({ type: 'role', roleId, title: null })
  })
  if (rows.some((r) => r.subject === 'responsible')) {
    audiences.push({ type: 'responsible', title: 'Ответственный в карточке' })
  }
  if (rows.some((r) => r.subject === 'uploader')) {
    audiences.push({ type: 'uploader', title: 'Автор вложения' })
  }
  if (rows.some((r) => r.subject === 'route_approver')) {
    audiences.push({ type: 'route_approver', title: 'Согласующий по маршруту' })
  }
  return audiences
}

export function buildStageDiagramData(stageRules, { roleItems, fileTypeItems }) {
  const entries = normalizeStageRuleEntries(stageRules)
  const rules = entries.map(({ row, idx }) => {
    const subject = row.subject || 'all'
    const roles = subject === 'role' ? (row.roles_lnk || []) : []
    const access = mergeRuleAccess([{ row }])
    return {
      num: typeof idx === 'number' ? idx + 1 : null,
      subject,
      subjectLabel: subjectLabel(subject),
      rolesLabel: roles.length > 0
        ? roles.map((id) => choiceLabel(roleItems, id)).join(', ')
        : '—',
      typesLabel: fileTypeLabels(row.file_types_lnk, fileTypeItems).join(', '),
      access,
      perms: {
        read: !!row.can_read,
        upload: !!row.can_upload,
        view: !!row.can_view,
        download: !!row.can_download,
        delete: !!row.can_delete,
      },
    }
  })

  const fileTypeSlices = collectStageFileTypeSlices(stageRules, fileTypeItems)
  const audiences = audiencesForStage(stageRules).map((audience) => {
    const title = audienceTitle(audience, roleItems)
    const ownedEntries = rulesOwnedByAudience(stageRules, audience)
    const matchedRows = rulesMatchingAudience(stageRules, audience)
    const ownedNums = ruleNumbers(ownedEntries)
    const totalEntries = entries.filter(({ row }) => matchedRows.includes(row))
    const totalNums = ruleNumbers(totalEntries)

    const byFileType = fileTypeSlices.map((slice) => {
      const filtered = entries.filter(
        ({ row }) => ruleAppliesToFileType(row, slice.key)
          && matchedRows.includes(row),
      )
      return {
        sliceKey: slice.key,
        sliceLabel: slice.label,
        ruleNums: ruleNumbers(filtered),
        access: mergeRuleAccess(filtered),
      }
    })

    return {
      id: `${audience.type}-${audience.roleId || ''}`,
      title,
      ownedNums,
      totalNums,
      ownedAccess: mergeRuleAccess(ownedEntries),
      totalAccess: mergeRuleAccess(matchedRows.map((row) => ({ row }))),
      byFileType,
    }
  })

  return { rules, audiences, fileTypeSlices }
}
